import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import * as Yup from 'yup';

import { ccpa } from '../../services';
import './ccpa-data-request.scss';

const defaultFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  requesterType: '',
  state: '',
  tellMe: false,
  doNotSell: false,
  deleteInfo: false,
  sendMe: false,
  other: false,
  otherText: '',
};

const CCPADataRequest = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');

  const onSubmit = async (values) => {
    setSubmitState('');

    if (
      !(values.tellMe || values.doNotSell || values.deleteInfo || values.sendMe || values.other)
    ) {
      setErrorMessage('Error: Please check at least one request box.');
      setSubmitState('failed');

      return;
    }

    try {
      await ccpa.submitDataRequest(values);
      setSubmitState('success');
    } catch (e) {
      Sentry.captureException(e);
      setErrorMessage(
        'We had a problem processing your request. Please try again or email us at privacy@swellenergy.com, call 1-888-465-1784, or write to us at 1515 7th St. #049, Santa Monica, CA 90401.'
      );
      setSubmitState('failed');
    }
  };

  const containerClasses = ['ccpadr'];

  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const successMessage =
    props.blok.success_message || 'You have successfully submitted your request.';

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
              firstName: Yup.string().required('Required'),
              lastName: Yup.string().required('Required'),
              email: Yup.string().required('Required').email('Enter a valid e-mail address'),
              requesterType: Yup.string().required('Required'),
              state: Yup.string().required('Required'),
            })}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  <div className="field">
                    <Field name="firstName" placeholder="First Name" />
                    <ErrorMessage className="error" component="div" name="firstName" />
                  </div>
                  <div className="field">
                    <Field name="lastName" placeholder="Last Name" />
                    <ErrorMessage className="error" component="div" name="lastName" />
                  </div>
                  <div className="field">
                    <Field name="email" placeholder="Email Address" />
                    <ErrorMessage className="error" component="div" type="email" name="email" />
                  </div>
                  <div className="field">
                    <Field name="requesterType" as="select" placeholder="I am a:">
                      <option value="" disabled selected>
                        I am a:
                      </option>
                      <option value="Customer">Customer</option>
                      <option value="Employee">Employee</option>
                      <option value="Job Applicant">Job Applicant</option>
                      <option value="Vendor">Vendor</option>
                      <option value="Ad/Email Recipient">Ad/Email Recipient</option>
                      <option value="Other">Other</option>
                    </Field>
                    <ErrorMessage className="error" component="div" name="requesterType" />
                  </div>
                  <div className="field">
                    <Field name="state" as="select" placeholder="State of Residence:">
                      <option value="" disabled selected>
                        State of Residence:
                      </option>
                      <option value="Other">Other</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="DC">District Of Columbia</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </Field>
                    <ErrorMessage className="error" component="div" name="state" />
                  </div>
                  <h4>My privacy request(s)</h4>
                  <div className="field">
                    <Field name="tellMe" id="tellMe" type="checkbox" />{' '}
                    <label htmlFor="tellMe">
                      Tell me more about the data you collect, disclose and/or sell
                    </label>
                  </div>
                  <div className="field">
                    <Field name="doNotSell" id="doNotSell" type="checkbox" />{' '}
                    <label htmlFor="doNotSell">
                      Do not sell my personal information (if applicable)
                    </label>
                  </div>
                  <div className="field">
                    <Field name="deleteInfo" id="deleteInfo" type="checkbox" />{' '}
                    <label htmlFor="deleteInfo">Delete my personal information</label>
                  </div>
                  <div className="field">
                    <Field name="sendMe" id="sendMe" type="checkbox" />{' '}
                    <label htmlFor="sendMe">Send me a copy of my personal information</label>
                  </div>
                  <div className="field">
                    <Field name="other" id="other" type="checkbox" />{' '}
                    <label htmlFor="other">Other</label>
                  </div>
                  <div className="field">
                    {formik.values.other && (
                      <Field
                        name="otherText"
                        placeholder="Other questions, comments, or requests"
                        maxLength="255"
                      />
                    )}
                  </div>
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  <span>
                    By clicking "Affirm &amp; Submit" you affirm the information above is accurate
                    and acknowledge that we may need to verify your identity and your state of
                    residence to determine if we can fulfill your request.
                  </span>
                  <button className={`btn-blue`} disabled={formik.isSubmitting} type="submit">
                    {buttonText}
                  </button>
                </Form>
              );
            }}
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

export default CCPADataRequest;
